import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetConversation } from '@/Domain/hooks/Conversation/useGetConversation.hook';

/**
 * Holds the conversation the list is windowed around when the Inbox is opened via a deeplink.
 *
 * The timestamp is frozen rather than re-read because replying to the anchored conversation
 * rewrites its own last_message_timestamp, and a live one would snap the window to the newest page.
 */
export const useConversationListAnchor = (
  conversationId: string | undefined,
  filtersKey: string,
) => {
  // Clicking a row must not re-anchor, or the list reshuffles under the operator's cursor.
  const [anchorId, setAnchorId] = useState<string | null>(conversationId ?? null);
  const filtersKeyRef = useRef(filtersKey);
  const frozenRef = useRef<{ id: string; lastMessageAt: string } | null>(null);
  const settledRef = useRef<{ id: string; found: boolean } | null>(null);

  useEffect(() => {
    if (filtersKeyRef.current === filtersKey) return;
    filtersKeyRef.current = filtersKey;
    setAnchorId(null);
  }, [filtersKey]);

  useEffect(() => {
    // Back at the list root, the auto-select effect has to pick the genuinely newest conversation.
    if (conversationId == null) setAnchorId(null);
  }, [conversationId]);

  const { conversation, isSettled, isError, refetch } = useGetConversation(anchorId ?? '');

  const hasSettled = settledRef.current?.id === anchorId;
  const wasAnchorFound = settledRef.current?.id === anchorId && settledRef.current.found;
  // The first settled lookup always latches, whatever its outcome and wherever the route has got
  // to, because that is what releases the list. Only recovery from a failed one is conditional:
  // it exists for an operator still sitting on the deeplink, and once they have moved on, freezing
  // the original timestamp would swap the list out from under the row they are now reading.
  const canLatch = !hasSettled || (!wasAnchorFound && conversationId === anchorId);

  if (anchorId == null) {
    frozenRef.current = null;
    settledRef.current = null;
  } else if (isSettled && canLatch) {
    // Latched on the outcome of the request that just finished, not on the retained data a failed
    // refetch leaves behind. One-shot once it succeeds: re-opening after that would let a
    // background refetch blank a list the operator is working in, while a lookup that failed has
    // nothing worth protecting, so a later success still recovers a transiently lost window.
    const found = !isError && conversation?.id === anchorId;
    settledRef.current = { id: anchorId, found };
    if (found && conversation?.last_message_at) {
      frozenRef.current = { id: anchorId, lastMessageAt: conversation.last_message_at };
    }
  }

  const anchorLastMessageAt =
    frozenRef.current?.id === anchorId ? (frozenRef.current?.lastMessageAt ?? null) : null;
  const isAnchorFound = settledRef.current?.id === anchorId && settledRef.current.found;

  const clearAnchor = useCallback(() => setAnchorId(null), []);

  // Read through a ref so the identity survives the anchor being dropped: the socket handler
  // captures this once, and `refetch` ignores `enabled`, so an unguarded call would ask for the
  // empty conversation id the disabled lookup carries.
  const anchorIdRef = useRef(anchorId);
  useEffect(() => {
    anchorIdRef.current = anchorId;
  }, [anchorId]);

  const refetchAnchor = useCallback(async () => {
    if (anchorIdRef.current != null) await refetch();
  }, [refetch]);

  return {
    anchorId,
    anchorLastMessageAt,
    // Pins the row when the anchor matches neither window: it received a new message, it is
    // outside the active tab, or its last_message_timestamp is null and the API substituted
    // last_updated_timestamp for it. Gated on the first lookup having found it, so a deeplink to
    // a deleted conversation cannot pin a retained cache entry into the fallback list.
    anchorConversation: isAnchorFound && conversation?.id === anchorId ? conversation : undefined,
    // The list must not fetch an unanchored first page and then re-key once the anchor resolves.
    // A failed lookup resolves too, so a dead deeplink falls back to the unanchored list.
    isAnchorResolved: anchorId == null || settledRef.current?.id === anchorId,
    clearAnchor,
    // The pinned row is served from this lookup, so it goes stale unless it refreshes with the
    // two list halves.
    refetchAnchor,
  };
};
